import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import InCallTypeCountView from "./InCallTypeCountView";
import D3EDate from "../classes/D3EDate";
import AdminOrdersRequest from "../models/AdminOrdersRequest";
import OutCallTypeCountView from "./OutCallTypeCountView";
import Admin from "../models/Admin";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import Customer from "../models/Customer";
import ListWrapper from "../utils/ListWrapper";
import RegisterUsersCountView from "./RegisterUsersCountView";
import Duration from "../core/Duration";
import TextView from "./TextView";
import Customers from "../classes/Customers";
import TotalOrdersCountView from "./TotalOrdersCountView";
import CollectionUtils from "../utils/CollectionUtils";
import DropDown from "./DropDown";
import OrderStatus from "../classes/OrderStatus";
import ServiceCallType from "../classes/ServiceCallType";
import Pie2DChart from "./Pie2DChart";
import WidgetWrapper from "./WidgetWrapper";
import CustomerUtils from "../classes/CustomerUtils";
import ActiveUsersCountView from "./ActiveUsersCountView";
import AdminOrders from "../classes/AdminOrders";
import Bar2DChart from "./Bar2DChart";
import Order from "../models/Order";
import DateTime from "../core/DateTime";
import TeleCallTypeCountView from "./TeleCallTypeCountView";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface AdminDashboardPage2Props extends BaseUIProps {
  key?: string;
  admin: Admin;
}

class _AdminDashboardPage2State extends ObservableComponent<AdminDashboardPage2Props> {
  static defaultProps = { admin: null };
  dropDownRefFocusNode: ui.FocusNode = new ui.FocusNode();
  ordersList: Array<Order> = ListWrapper.widget(this, "ordersList");
  query: AdminOrders = null;
  filterValue: string = "";
  customersList: Customers = null;
  customers: Array<Customer> = ListWrapper.widget(this, "customers");
  fromDate: D3EDate = null;
  toDate: D3EDate = null;
  request: AdminOrdersRequest = null;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminDashboardPage2Props) {
    super(props);

    this.initState();
  }
  public get admin(): Admin {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(["query", "query.items"], this.computeOrdersList);

    this.computeOrdersList();

    this.on(
      [
        "request",
        "request.applyStatus",
        "request.business",
        "request.fromDate",
        "request.orderDate",
        "request.status",
        "request.toDate",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.computeCustomersList();

    this.on(["customersList", "customersList.items"], this.computeCustomers);

    this.computeCustomers();

    this.on(
      [
        "customers",
        "filterValue",
        "ordersList",
        "ordersList.callType",
        "ordersList.createdDate",
        "ordersList.customer",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminDashboardPage2Props): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public setOrdersList(val: Array<Order>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.ordersList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("ordersList", this.ordersList, val);

    this.ordersList.clear();

    this.ordersList.addAll(val);

    this.fire("ordersList", this);
  }
  public addToOrdersList(val: Order, index: number = -1): void {
    if (index === -1) {
      if (!this.ordersList.contains(val)) this.ordersList.add(val);
    } else {
      this.ordersList.remove(this.ordersList.elementAt(index));

      this.ordersList.add(val);
    }

    this.fire("ordersList", this, val, true);

    this.updateObservable("ordersList", null, val);
  }
  public removeFromOrdersList(val: Order): void {
    this.ordersList.remove(val);

    this.fire("ordersList", this, val, false);

    this.removeObservable("ordersList", val);
  }
  public computeOrdersList = (): void => {
    try {
      this.setOrdersList(
        Array.from(this.query !== null ? this.query.items : [])
      );
    } catch (exception) {
      console.log(" exception in computeOrdersList : " + exception.toString());

      this.setOrdersList([]);
    }
  };
  public setQuery(val: AdminOrders): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getAdminOrders(
          UsageConstants.QUERY_GETADMINORDERS_ADMINDASHBOARDPAGE2_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setFilterValue(val: string): void {
    let isValChanged: boolean = this.filterValue !== val;

    if (!isValChanged) {
      return;
    }

    this.filterValue = val;

    this.fire("filterValue", this);
  }
  public setCustomersList(val: Customers): void {
    let isValChanged: boolean = this.customersList !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("customersList", this.customersList, val);

    MessageDispatch.get().dispose(this.customersList);

    this.customersList = val;

    this.fire("customersList", this);
  }
  public computeCustomersList = async (): Promise<void> => {
    try {
      this.setCustomersList(
        await Query.get().getCustomers(
          UsageConstants.QUERY_GETCUSTOMERS_ADMINDASHBOARDPAGE2_PROPERTIES_CUSTOMERSLIST_COMPUTATION,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeCustomersList : " + exception.toString()
      );

      this.setCustomersList(null);
    }
  };
  public setCustomers(val: Array<Customer>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.customers,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("customers", this.customers, val);

    this.customers.clear();

    this.customers.addAll(val);

    this.fire("customers", this);
  }
  public addToCustomers(val: Customer, index: number = -1): void {
    if (index === -1) {
      if (!this.customers.contains(val)) this.customers.add(val);
    } else {
      this.customers.remove(this.customers.elementAt(index));

      this.customers.add(val);
    }

    this.fire("customers", this, val, true);

    this.updateObservable("customers", null, val);
  }
  public removeFromCustomers(val: Customer): void {
    this.customers.remove(val);

    this.fire("customers", this, val, false);

    this.removeObservable("customers", val);
  }
  public computeCustomers = (): void => {
    try {
      this.setCustomers(
        Array.from(
          this.customersList.items.isNotEmpty ? this.customersList.items : []
        )
      );
    } catch (exception) {
      console.log(" exception in computeCustomers : " + exception.toString());

      this.setCustomers([]);
    }
  };
  public setFromDate(val: D3EDate): void {
    let isValChanged: boolean = this.fromDate !== val;

    if (!isValChanged) {
      return;
    }

    this.fromDate = val;

    this.fire("fromDate", this);
  }
  public setToDate(val: D3EDate): void {
    let isValChanged: boolean = this.toDate !== val;

    if (!isValChanged) {
      return;
    }

    this.toDate = val;

    this.fire("toDate", this);
  }
  public setRequest(val: AdminOrdersRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            TextView({
              data: "Dashboard",
              style: new ui.TextStyle({
                fontSize: cStyle.tTextViewHeadlineTwoFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineTwoFontWeightOn,
              }),
              className: "xb1",
              key: "0",
            }),
            ui.Container({
              margin: ui.EdgeInsets.symmetric({
                horizontal: 10.0,
                vertical: 0.0,
                transitions: new Map(),
              }),
              width: 200,
              child: DropDown<string>({
                items: ["Today", "Yesterday", "Current Week", "Last Month"],
                value: this.filterValue,
                onChanged: (value) => {
                  this.dropdownHandler(value);
                },
                onFocusChange: (val) => {},
                builder: (context, item) => {
                  return TextView({ data: item });
                },
                focusNode: this.dropDownRefFocusNode,
              }),
              key: "1",
              className: "x0cf hc",
            }),
          ],
          className: "xf70 hc h",
          key: "0",
        }),
        ui.Row({
          children: [
            ui.Container({
              expand: true,
              padding: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              child: InCallTypeCountView({
                count: this.ordersList.where(
                  (o) => o.callType === ServiceCallType.InCall
                ).length,
              }),
              key: "0",
              className: "xe8 hc h",
            }),
            ui.Container({
              expand: true,
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              child: OutCallTypeCountView({
                count: this.ordersList.where(
                  (o) => o.callType === ServiceCallType.OutCall
                ).length,
              }),
              key: "1",
              className: "xac hc h",
            }),
            ui.Container({
              expand: true,
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              child: TeleCallTypeCountView({
                count: this.ordersList.where(
                  (o) => o.callType === ServiceCallType.TeleCall
                ).length,
              }),
              key: "2",
              className: "xab hc h",
            }),
            ui.Container({
              expand: true,
              child: TotalOrdersCountView({ count: this.ordersList.length }),
              key: "3",
              className: "x77d hc h",
            }),
          ],
          className: "xd7f hc h",
          key: "1",
        }),
        ui.Row({
          children: [
            ui.Container({
              expand: true,
              height: 300,
              margin: ui.EdgeInsets.fromLTRB(5.0, 5.0, 15.0, 5.0, new Map()),
              child: WidgetWrapper({
                title: "Trends",
                child: ui.Column({
                  children: [
                    ui.Row({
                      children: [
                        ui.Container({ className: "x0d2 hc vc", key: "0" }),
                        TextView({
                          data: "Categories",
                          className: "x68",
                          key: "1",
                        }),
                      ],
                      className: "x71 hc h",
                      key: "0",
                    }),
                    ui.Container({
                      height: 300,
                      width: 300,
                      child: Bar2DChart({ data: [] }),
                      key: "1",
                      className: "xf83 hc vc",
                    }),
                  ],
                  className: "xe01 hc vc h v",
                }),
              }),
              key: "0",
              className: "x05 hc vc h",
            }),
            ui.Container({
              expand: true,
              height: 300,
              margin: ui.EdgeInsets.all(5.0, new Map()),
              child: WidgetWrapper({
                title: "Performance Metrics",
                child: ui.Row({
                  children: [
                    ui.Column({
                      children: [
                        ui.Container({
                          height: 350,
                          width: 300,
                          child: Pie2DChart({ data: [] }),
                          key: "0",
                          className: "x6c hc vc",
                        }),
                      ],
                      className: "x7cd vc v",
                      key: "0",
                    }),
                    ui.Column({
                      children: [
                        ui.Row({
                          children: [
                            ui.Container({ className: "xc76 hc vc", key: "0" }),
                            TextView({
                              data: "Poor",
                              className: "x2f3",
                              key: "1",
                            }),
                          ],
                          className: "xbb1",
                          key: "0",
                        }),
                        ui.Row({
                          children: [
                            ui.Container({ className: "x6c4 hc vc", key: "0" }),
                            TextView({
                              data: "Sufficient",
                              className: "x27",
                              key: "1",
                            }),
                          ],
                          className: "x4e0",
                          key: "1",
                        }),
                        ui.Row({
                          children: [
                            ui.Container({ className: "xec1 hc vc", key: "0" }),
                            TextView({
                              data: "Good",
                              className: "x1c5",
                              key: "1",
                            }),
                          ],
                          className: "x9f5",
                          key: "2",
                        }),
                        ui.Row({
                          children: [
                            ui.Container({ className: "x46 hc vc", key: "0" }),
                            TextView({
                              data: "Very Good",
                              className: "x741",
                              key: "1",
                            }),
                          ],
                          className: "x648",
                          key: "3",
                        }),
                      ],
                      className: "xa8 vc v",
                      key: "1",
                    }),
                  ],
                  className: "x9d hc vc h v",
                }),
              }),
              key: "1",
              className: "x1145 hc vc h",
            }),
          ],
          className: "xdc hc vc h",
          key: "2",
        }),
        ui.Row({
          children: [
            ui.Container({
              expand: true,
              height: 300,
              margin: ui.EdgeInsets.fromLTRB(5.0, 5.0, 15.0, 5.0, new Map()),
              child: WidgetWrapper({
                title: "Revenue Reports",
                child: ui.Column({
                  children: [
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.start,
                      children: [
                        ui.Row({
                          children: [
                            ui.Container({ className: "x13 hc vc", key: "0" }),
                            TextView({
                              data: "Active Users",
                              className: "x81",
                              key: "1",
                            }),
                          ],
                          key: "0",
                        }),
                        ui.Row({
                          children: [
                            ui.Container({ className: "x65 hc vc", key: "0" }),
                            TextView({
                              data: "Register Users",
                              className: "xba4",
                              key: "1",
                            }),
                          ],
                          className: "x21",
                          key: "1",
                        }),
                      ],
                      className: "x4b hc h",
                      key: "0",
                    }),
                  ],
                  className: "x406 hc vc h v",
                }),
              }),
              key: "0",
              className: "xc6 hc vc h",
            }),
            ui.Row({
              children: [
                ui.Container({
                  expand: true,
                  height: 300,
                  margin: ui.EdgeInsets.fromLTRB(5.0, 5.0, 7.5, 5.0, new Map()),
                  child: ActiveUsersCountView({
                    count: CustomerUtils.getCustomersWithCompletedOrders(
                      this.ordersList
                    ).length,
                  }),
                  key: "0",
                  className: "xff6 hc vc h",
                }),
                ui.Container({
                  expand: true,
                  height: 300,
                  margin: ui.EdgeInsets.fromLTRB(7.5, 5.0, 5.0, 5.0, new Map()),
                  child: RegisterUsersCountView({
                    count: this.customers.length,
                  }),
                  key: "1",
                  className: "x55b hc vc h",
                }),
              ],
              className: "x457 hc vc h",
              key: "1",
            }),
          ],
          className: "x61c hc vc h",
          key: "3",
        }),
        ui.Container({
          expand: true,
          height: 300,
          margin: ui.EdgeInsets.fromLTRB(5.0, 5.0, 0.0, 10.0, new Map()),
          child: WidgetWrapper({
            title: "Revenue Reports",
            child: ui.Column({
              children: [
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.start,
                  children: [
                    ui.Row({
                      children: [
                        ui.Container({ className: "xa7 hc vc", key: "0" }),
                        TextView({
                          data: "Active Users",
                          className: "xf72",
                          key: "1",
                        }),
                      ],
                      key: "0",
                    }),
                    ui.Row({
                      children: [
                        ui.Container({ className: "xe70 hc vc", key: "0" }),
                        TextView({
                          data: "Register Users",
                          className: "x42d",
                          key: "1",
                        }),
                      ],
                      className: "x91",
                      key: "1",
                    }),
                  ],
                  className: "x2a hc h",
                  key: "0",
                }),
              ],
              className: "x474 hc vc h v",
            }),
          }),
          key: "4",
          className: "x09 hc vc h",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminDashboardPage2 x06a hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new AdminOrdersRequest({
        business: null,
        applyStatus: true,
        status: OrderStatus.Completed,
        fromDate: D3EDate.now(),
        toDate: D3EDate.now(),
      })
    );
  };
  public onSetDatesInRequest = (): void => {
    this.request.setFromDate(this.fromDate);

    this.request.setToDate(this.toDate);
  };
  public dropdownHandler = (value: string): void => {
    this.setFilterValue(value);

    if (this.filterValue === "Today") {
      this.setFilterValue("Today");

      this.request.setFromDate(D3EDate.now());

      this.setToDate(D3EDate.now());

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Yesterday") {
      this.setFilterValue("Yesterday");

      let today: D3EDate = D3EDate.now();

      this.setFromDate(
        D3EDate.of(today.year, today.month, today.dayOfMonth - 1)
      );

      this.setToDate(D3EDate.of(today.year, today.month, today.dayOfMonth - 1));

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Current Week") {
      this.setFilterValue("Current Week");

      let dateTime: DateTime = DateTime.now();

      let firstDayOfWeek: DateTime = dateTime.subtract(
        new Duration({ days: dateTime.weekday - 1 })
      );

      this.setFromDate(
        D3EDate.of(
          firstDayOfWeek.year,
          firstDayOfWeek.month,
          firstDayOfWeek.day
        )
      );

      this.setToDate(D3EDate.now());

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Last Month") {
      this.setFilterValue("Last Month");

      let today: D3EDate = D3EDate.now();

      this.setFromDate(D3EDate.of(today.year, today.month - 1, 1));

      this.setToDate(D3EDate.of(today.year, today.month - 1, 1));

      this.onSetDatesInRequest();
    }
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    MessageDispatch.get().dispose(this.customersList);

    super.dispose();
  }
}
export default function AdminDashboardPage2(props: AdminDashboardPage2Props) {
  return React.createElement(_AdminDashboardPage2State, {
    ..._AdminDashboardPage2State.defaultProps,
    ...props,
  });
}
